import { Component, Input, OnInit } from '@angular/core';
import { TranslateService }         from '@ngx-translate/core';

import { BigDecimalService }            from '../core/services/big-decimal.service';
import { CodeTypeModel }                from '../core/models/code-type';
import { CompanyListsActionsComponent } from './company-lists.actions.component';
import { CompanyListsService }          from './company-lists.service';
import { CompanyService }               from '../companies/company.service';
import { ConfirmService }               from '../messages/confirm/confirm.service';
import { MessagesService }              from '../messages/messages.service';
import { ProductCategoryModel }         from './typings/product-category';
import { RemoveComponent }              from '../table/cell-components/actions/remove.component';
import { Table }                        from '../table/types/table';
import { TableColumn }                  from '../table/types/table-column';
import { TableSelectControlComponent }  from '../table/cell-components/controls/select-control/table-select-control.component';
import { TableTextControlComponent }    from '../table/cell-components/controls/text-control/table-text-control.component';
import { XeroService }                  from '../xero/xero.service';
import { XeroTaxRateModel }             from '../xero/models/xero.tax-rate';

@Component({
  selector   : 'wor-company-lists-product-categories',
  template: require('./company-lists.product-categories.component.html')
})
export class CompanyListsProductCategoriesComponent implements OnInit {
  @Input() productCategories: Array<ProductCategoryModel> = [];

  originalCategories: Map<string, ProductCategoryModel> = new Map();
  qboTaxRates       : Array<any>                        = [];
  qboTaxTypeOptions : Array<CodeTypeModel>              = [];
  tableSettings     : Table                             = {};
  xeroTaxRates      : Array<XeroTaxRateModel>           = [];
  xeroTaxTypeOptions: Array<CodeTypeModel>              = [];

  collapsed = false;

  constructor (
    private bigDecimalService  : BigDecimalService,
    private companyListsService: CompanyListsService,
    public  companyService     : CompanyService,
    private confirmService     : ConfirmService,
    private messagesService    : MessagesService,
    private translateService   : TranslateService,
    private xeroService        : XeroService
  ) {}

  ngOnInit () : void {
    if ( this.isQuickbooksTaxTypeAvailable() ) {
      this.companyListsService.getQuickbooksTaxRates()
      .then(( taxRates : Array<any> ) => {
        this.qboTaxRates = taxRates;

        this.qboTaxTypeOptions = taxRates.map(taxRate => ({
          code       : taxRate.id,
          description: taxRate.name
        }));

        this.tableSettings = this.getTableSettings();
        this.refreshTable();
      })
      .catch(error => this.messagesService.error(error));
    }
    else if ( this.isXeroTaxTypeAvailable() ) {
      this.xeroService.getTaxRates()
      .then(( taxRates : Array<XeroTaxRateModel> ) => {
        this.xeroTaxRates = taxRates;

        this.xeroTaxTypeOptions = taxRates.map(taxRate => ({
          code       : taxRate.tax_type,
          description: taxRate.name
        }));

        this.tableSettings = this.getTableSettings();
        this.refreshTable();
      })
      .catch(error => this.messagesService.error(error));
    }
    else {
      this.tableSettings = this.getTableSettings();
    }
  }

  addProductCategory () : void {
    this.productCategories = [
      ...this.productCategories,
      {
        description           : '',
        focusField            : 'name',
        editing               : true,
        name                  : '',
        purchase_tax_rate     : '',
        qbo_purchase_tax_type : '',
        qbo_sales_tax_type    : '',
        sales_tax_rate        : '',
        xero_purchase_tax_type: '',
        xero_sales_tax_type   : ''
      }
    ];
  }

  clearFocusField ( category : ProductCategoryModel, field : 'name' | 'description' ) : void {
    if ( category?.focusField === field ) {
      category.focusField = undefined;
    }
  }

  deleteProductCategory ( category : ProductCategoryModel ) : void {
    if ( !category.id ) {
      this.productCategories = this.productCategories.filter(productCategory => productCategory !== category);

      return;
    }

    this.confirmService.generic(this.translateService.instant('JS_SPACE.CONFIRM.DELETE_TEMPLATE'))
    .then(() => this.companyListsService.deleteProductCategory(category))
    .then(() => {
      this.productCategories = this.productCategories.filter(productCategory => productCategory.id !== category.id);

      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.DELETED.PRODUCT_CATEGORY'), 'success');
    })
    .catch(error => this.messagesService.error(error));
  }

  getTableSettings () : Table {
    const columns : Array<TableColumn> = [
      {
        component: {
          bindings: {
            outputs: {
              onRemoveClick: ( category : ProductCategoryModel ) => this.deleteProductCategory(category)
            }
          },
          enabled: true,
          factory: RemoveComponent
        },
        sortable: false
      },
      {
        component: {
          bindings: {
            inputs: {
              disabled : ( category : ProductCategoryModel ) => !category.editing || !!category.id,
              giveFocus: ( category : ProductCategoryModel ) => category.focusField === 'name',
              model    : 'name',
              name     : 'product_category_name',
              required : true
            },
            outputs: {
              focus: ( category : ProductCategoryModel ) => this.clearFocusField(category, 'name')
            }
          },
          enabled: true,
          factory: TableTextControlComponent
        },
        field : 'name',
        header: {
          text: this.translateService.instant('COMPANY_SPACE.SETTINGS.CODE')
        }
      },
      {
        component: {
          bindings: {
            inputs: {
              disabled : ( category : ProductCategoryModel ) => !category.editing,
              giveFocus: ( category : ProductCategoryModel ) => category.focusField === 'description',
              model    : 'description',
              name     : 'product_category_description',
              required : true
            },
            outputs: {
              focus: ( category : ProductCategoryModel ) => this.clearFocusField(category, 'description')
            }
          },
          enabled: true,
          factory: TableTextControlComponent
        },
        field : 'description',
        header: {
          text: this.translateService.instant('GENERAL_SPACE.FIELD.DESCRIPTION')
        }
      }
    ];

    if ( this.isTaxRateAvailable() ) {
      columns.push(
        {
          component: {
            bindings: {
              inputs: {
                disabled: ( category : ProductCategoryModel ) => !this.isTaxRateEditable(category),
                model   : 'sales_tax_rate',
                name    : 'product_category_sales_tax_rate',
                type    : 'number'
              }
            },
            enabled: true,
            factory: TableTextControlComponent
          },
          field : 'sales_tax_rate',
          header: {
            text: this.translateService.instant('COMPANY_SPACE.LISTS.SALES_TAX')
          }
        },
        {
          component: {
            bindings: {
              inputs: {
                disabled: ( category : ProductCategoryModel ) => !this.isTaxRateEditable(category),
                model   : 'purchase_tax_rate',
                name    : 'product_category_purchase_tax_rate',
                type    : 'number'
              }
            },
            enabled: true,
            factory: TableTextControlComponent
          },
          field : 'purchase_tax_rate',
          header: {
            text: this.translateService.instant('COMPANY_SPACE.LISTS.PURCHASES_TAX')
          }
        }
      );
    }

    if ( this.isQuickbooksTaxTypeAvailable() ) {
      columns.push(
        {
          component: {
            bindings: {
              inputs: {
                disabled   : ( category : ProductCategoryModel ) => !category.editing,
                isClearable: true,
                model      : 'qbo_sales_tax_type',
                name       : 'product_category_qbo_sales_tax_type',
                options    : this.qboTaxTypeOptions
              },
              outputs: {
                onChange: ( category : ProductCategoryModel ) => this.updateTax(category)
              }
            },
            enabled: true,
            factory: TableSelectControlComponent
          },
          header: {
            text: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.ACCOUNTING.SALES_TAX_TYPE')
          }
        },
        {
          component: {
            bindings: {
              inputs: {
                disabled   : ( category : ProductCategoryModel ) => !category.editing,
                isClearable: true,
                model      : 'qbo_purchase_tax_type',
                name       : 'product_category_qbo_purchase_tax_type',
                options    : this.qboTaxTypeOptions
              },
              outputs: {
                onChange: ( category : ProductCategoryModel ) => this.updateTax(category)
              }
            },
            enabled: true,
            factory: TableSelectControlComponent
          },
          header: {
            text: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.ACCOUNTING.PURCHASES_TAX_TYPE')
          }
        }
      );
    }
    else if ( this.isXeroTaxTypeAvailable() ) {
      columns.push(
        {
          component: {
            bindings: {
              inputs: {
                disabled   : ( category : ProductCategoryModel ) => !category.editing,
                isClearable: true,
                model      : 'xero_sales_tax_type',
                name       : 'product_category_xero_sales_tax_type',
                options    : this.xeroTaxTypeOptions
              },
              outputs: {
                onChange: ( category : ProductCategoryModel ) => this.updateTax(category)
              }
            },
            enabled: true,
            factory: TableSelectControlComponent
          },
          header: {
            text: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.ACCOUNTING.SALES_TAX_TYPE')
          }
        },
        {
          component: {
            bindings: {
              inputs: {
                disabled   : ( category : ProductCategoryModel ) => !category.editing,
                isClearable: true,
                model      : 'xero_purchase_tax_type',
                name       : 'product_category_xero_purchase_tax_type',
                options    : this.xeroTaxTypeOptions
              },
              outputs: {
                onChange: ( category : ProductCategoryModel ) => this.updateTax(category)
              }
            },
            enabled: true,
            factory: TableSelectControlComponent
          },
          header: {
            text: this.translateService.instant('COMPANY_SPACE.INTEGRATIONS.ACCOUNTING.PURCHASES_TAX_TYPE')
          }
        }
      );
    }

    columns.push({
      classes  : 'text-right',
      component: {
        bindings: {
          outputs: {
            onCancelClick: ( category : ProductCategoryModel ) => this.toggleEditing(category, false),
            onEditClick  : ( category : ProductCategoryModel ) => this.toggleEditing(category, true),
            onSaveClick  : ( category : ProductCategoryModel ) => this.save(category)
          }
        },
        enabled: true,
        factory: CompanyListsActionsComponent
      },
      sortable: false
    });

    return {
      columns,
      empty: {
        text: this.translateService.instant('COMPANY_SPACE.LISTS.GRID.NO_PRODUCT_CATEGORIES')
      },
      pagination: {
        enabled: false
      },
      rows: {
        highlight: {
          enabled: false
        }
      },
      sorting: {
        enabled: false
      }
    };
  }

  getTaxRate ( taxRate : any ) : any {
    return taxRate || taxRate === 0
      ? this.bigDecimalService.get(taxRate)
      : null;
  }

  isQuickbooksTaxTypeAvailable () : boolean {
    return this.companyService.hasIntegration('quickbooks')
      && !this.companyService.hasIntegration('xero')
      && !this.companyService.hasIntegration('myob');
  }

  isTaxRateAvailable () : boolean {
    return !this.companyService.usesMultiTax()
      && !this.companyService.hasIntegration('myob');
  }

  isTaxRateEditable ( category : ProductCategoryModel ) : boolean {
    return !!category.editing
      && !this.isQuickbooksTaxTypeAvailable()
      && !this.isXeroTaxTypeAvailable();
  }

  isXeroTaxTypeAvailable () : boolean {
    return this.companyService.hasIntegration('xero')
      && !this.companyService.hasIntegration('quickbooks')
      && !this.companyService.hasIntegration('myob');
  }

  refreshTable () : void {
    this.productCategories = this.productCategories.map(productCategory => ({ ...productCategory }));
  }

  save ( category : ProductCategoryModel ) : void {
    if ( !category.name || !category.description ) {
      this.messagesService.error(this.translateService.instant('JS_SPACE.MESSAGES.INVALID_INPUT'));

      return;
    }

    if ( this.companyService.hasIntegration('myob') ) {
      category.purchase_tax_rate = null;
      category.sales_tax_rate    = null;
    }

    this.updateTax(category);

    const previousCategory  = category.id ? this.originalCategories.get(category.id) : null;
    const purchasesTaxRate  = this.getTaxRate(category.purchase_tax_rate);
    const salesTaxRate      = this.getTaxRate(category.sales_tax_rate);
    const hasZeroTaxRate    = this.isTaxRateAvailable()
      && (( salesTaxRate && salesTaxRate.isZero() ) || ( purchasesTaxRate && purchasesTaxRate.isZero() ));

    Promise.resolve()
    .then(() => hasZeroTaxRate
      ? this.confirmService.generic(this.translateService.instant('COMPANY_SPACE.LISTS.TAX_RATE_IS_ZERO'))
      : Promise.resolve(true)
    )
    .then(() => this.companyListsService.saveProductCategory(category))
    .then(( result : Array<ProductCategoryModel> | ProductCategoryModel ) => {
      if ( result instanceof Array ) {
        const savedCategory = result.find(productCategory => productCategory.id === category.id);

        if ( savedCategory ) {
          Object.assign(category, savedCategory);
        }

        category.editing    = false;
        category.focusField = undefined;

        this.refreshTable();
      }
      else {
        this.productCategories = this.productCategories.map(productCategory => productCategory === category
          ? {
              ...result,
              editing   : false,
              focusField: undefined
            }
          : productCategory
        );
      }

      this.messagesService.show(this.translateService.instant('JS_SPACE.MESSAGES.SAVED.PRODUCT_CATEGORY'), 'success');
    })
    .catch(error => {
      if ( error ) {
        this.messagesService.error(error);

        return;
      }

      if ( previousCategory ) {
        category.sales_tax_rate = salesTaxRate?.isZero()
          ? previousCategory.sales_tax_rate
          : category.sales_tax_rate;

        category.purchase_tax_rate = purchasesTaxRate?.isZero()
          ? previousCategory.purchase_tax_rate
          : category.purchase_tax_rate;

        this.refreshTable();
      }
    });
  }

  toggleEditing ( category : ProductCategoryModel, editing : boolean ) : void {
    if ( editing ) {
      if ( category.id && !this.originalCategories.has(category.id) ) {
        this.originalCategories.set(category.id, { ...category, editing: false });
      }

      category.editing    = true;
      category.focusField = category.id
        ? 'description'
        : 'name';
    }
    else if ( !category.id ) {
      this.deleteProductCategory(category);

      return;
    }
    else {
      const originalCategory = this.originalCategories.get(category.id);

      if ( originalCategory ) {
        Object.assign(category, originalCategory);

        this.originalCategories.delete(category.id);
      }

      category.editing    = false;
      category.focusField = undefined;
    }

    this.refreshTable();
  }

  updateTax ( category : ProductCategoryModel ) : void {
    if ( this.isQuickbooksTaxTypeAvailable() ) {
      category.purchase_tax_rate = category.qbo_purchase_tax_type
        ? this.qboTaxRates.find(rate => rate.id === category.qbo_purchase_tax_type)?.purchase_tax_value
        : null;

      category.sales_tax_rate = category.qbo_sales_tax_type
        ? this.qboTaxRates.find(rate => rate.id === category.qbo_sales_tax_type)?.sales_tax_value
        : null;
    }
    else if ( this.isXeroTaxTypeAvailable() ) {
      category.purchase_tax_rate = category.xero_purchase_tax_type
        ? this.xeroTaxRates.find(rate => rate.tax_type === category.xero_purchase_tax_type)?.effective_rate as string
        : null;

      category.sales_tax_rate = category.xero_sales_tax_type
        ? this.xeroTaxRates.find(rate => rate.tax_type === category.xero_sales_tax_type)?.effective_rate as string
        : null;
    }
  }
}
